import moment from "moment";
import Model from "./model";

const PAYMENT_INTERVAL = {
  YEAR: "year",
  MONTH: "month",
  DAY: "day",
};

const AUTO_RENEWAL = {
  ON: "on",
  OFF: "off",
};

const SUBSCRIPTION_STATUS = {
  EXPIRED: "expired",
  ACTIVE: "active",
};

/* eslint-disable no-unused-expressions */
class Subscription extends Model {
  constructor(organizationId = null, subscriptionId = null) {
    super();

    this._organizationId = organizationId;
    this._subscriptionId = subscriptionId;

    this._projectId;
    this._productResourceId;
    this._subscriptionType;
    this._paymentInterval;
    this._paymentIntervalCount;
    this._expiresAt;
    this._autoRenewal;
    this._subscriptionStatus;
    this._metadata;
    this._billingStartAt;
    this._trialEndAt;
    this._cancelableFrom;
    this._createdAt;
  }

  // organizationId
  get organizationId() {
    return this._organizationId;
  }

  set organizationId(value) {
    this._organizationId = value;
  }

  // subscriptionId
  get subscriptionId() {
    return this._subscriptionId;
  }

  set subscriptionId(value) {
    this._subscriptionId = value;
  }

  // projectId
  get projectId() {
    return this._projectId;
  }

  set projectId(value) {
    this._projectId = value;
  }

  // productResourceId
  get productResourceId() {
    return this._productResourceId;
  }

  set productResourceId(value) {
    this._productResourceId = value;
  }

  // subscriptionType
  get subscriptionType() {
    return this._subscriptionType;
  }

  set subscriptionType(value) {
    this._subscriptionType = value;
  }

  // paymentInterval
  get paymentInterval() {
    return this._paymentInterval;
  }

  set paymentInterval(value) {
    this._paymentInterval = value;
  }

  // paymentIntervalCount
  get paymentIntervalCount() {
    return this._paymentIntervalCount;
  }

  set paymentIntervalCount(value) {
    this._paymentIntervalCount = value;
  }

  // expiresAt
  get expiresAt() {
    return this._expiresAt;
  }

  set expiresAt(value) {
    this._expiresAt = value;
  }

  // autoRenewal
  get autoRenewal() {
    return this._autoRenewal;
  }

  set autoRenewal(value) {
    this._autoRenewal = value;
  }

  // statusCd
  get subscriptionStatus() {
    return this._subscriptionStatus;
  }

  set subscriptionStatus(value) {
    this._subscriptionStatus = value;
  }

  // metadata
  get metadata() {
    return this._metadata;
  }

  set metadata(value) {
    this._metadata = value;
  }

  // billingStartAt
  get billingStartAt() {
    return this._billingStartAt;
  }

  set billingStartAt(value) {
    this._billingStartAt = value;
  }

  // trialEndAt
  get trialEndAt() {
    return this._trialEndAt;
  }

  set trialEndAt(value) {
    this._trialEndAt = value;
  }

  // cancelableFrom
  get cancelableFrom() {
    return this._cancelableFrom;
  }

  set cancelableFrom(value) {
    this._cancelableFrom = value;
  }

  // createdAt
  get createdAt() {
    return this._createdAt;
  }

  set createdAt(value) {
    this._createdAt = value;
  }

  isRegisterd() {
    return this.subscriptionId !== null;
  }

  json() {
    return {
      ...super.json(),
      organizationId: this.organizationId,
      subscriptionId: this.subscriptionId,
      projectId: this.projectId,
      productResourceId: this.productResourceId,
      subscriptionType: this.subscriptionType,
      paymentInterval: this.paymentInterval,
      paymentIntervalCount: this.paymentIntervalCount,
      expiresAt: this.expiresAt,
      autoRenewal: this.autoRenewal,
      subscriptionStatus: this.subscriptionStatus,
      billingStartAt: this.billingStartAt,
      trialEndAt: this.trialEndAt,
      cancelableFrom: this.cancelableFrom,
      createdAt: this.createdAt,
    };
  }

  copy() {
    const subscription = new Subscription(this.organizationId, this.subscriptionId);
    subscription.copyProperties(this);
    return subscription;
  }

  copyProperties(subscription) {
    super.copyProperties(subscription);

    this.projectId = subscription.projectId;
    this.productResourceId = subscription.productResourceId;
    this.subscriptionType = subscription.subscriptionType;
    this.paymentInterval = subscription.paymentInterval;
    this.paymentIntervalCount = subscription.paymentIntervalCount;
    this.expiresAt = subscription.expiresAt;
    this.autoRenewal = subscription.autoRenewal;
    this.subscriptionStatus = subscription.subscriptionStatus;
    this.billingStartAt = subscription.billingStartAt;
    this.trialEndAt = subscription.trialEndAt;
    this.cancelableFrom = subscription.cancelableFrom;
    this.createdAt = subscription.createdAt;
  }

  getApiRequestJson() {
    const requestJosn = super.getApiRequestJson();

    Object.assign(requestJosn, {
      organization_id: this.organizationId,
      subscription_id: this.subscriptionId,
      project_id: this.projectId,
      product_resource_id: this.productResourceId,
      subscription_type: this.subscriptionType,
      payment_interval: this.paymentInterval,
      payment_interval_count: this.paymentIntervalCount,
      expires_at: this.expiresAt,
      auto_renewal: this.autoRenewal,
      subscription_status: this.subscriptionStatus,
      billing_start_at: this.billingStartAt,
      trial_end_at: this.trialEndAt,
      cancelable_from: this.cancelableFrom,
      created_at: this.createdAt,
    });
    return requestJosn;
  }

  setApiResponseJosn(item) {
    super.setApiResponseJosn(item);

    this.organizationId = item.organization_id;
    this.subscriptionId = item.subscription_id;
    this.projectId = item.project_id;
    this.productResourceId = item.product_resource_id;
    this.subscriptionType = item.subscription_type;
    this.paymentInterval = item.payment_interval;
    this.paymentIntervalCount = item.payment_interval_count;
    this.expiresAt = item.expires_at;
    this.autoRenewal = item.auto_renewal;
    this.subscriptionStatus = item.subscription_status;
    this.billingStartAt = item.billing_start_at;
    this.trialEndAt = item.trial_end_at;
    this.cancelableFrom = item.cancelable_from;
    this.createdAt = item.created_at;
  }

  isActive() {
    return this.subscriptionStatus === SUBSCRIPTION_STATUS.ACTIVE;
    // let res = false;
    // const dtNow = moment();
    // if (this.expiresAt && moment(this.expiresAt) >= dtNow) {
    //   res = true;
    // }
    // return res;
  }

  renewalAtText(format = "YYYY/MM/DD") {
    let res = "";
    if (this.expiresAt) {
      const renewalAt = moment(this.expiresAt).add(1, "second");
      res = renewalAt.format(format);
    }
    return res;
  }

  expiseAtText(format = "YYYY/MM/DD") {
    let res = "";
    if (this.expiresAt) {
      const renewalAt = moment(this.expiresAt);
      res = renewalAt.format(format);
    }
    return res;
  }

  isAutoRenewalActive() {
    return this.autoRenewal === AUTO_RENEWAL.ON;
  }

  autoRenewalText() {
    let res = "";
    if (this.autoRenewal === AUTO_RENEWAL.ON) {
      res = "自動更新";
    } else if (this.autoRenewal === AUTO_RENEWAL.OFF) {
      res = "契約なし";
    }
    return res;
  }

  paymentCycleText() {
    let res = "";
    if (this.paymentInterval === PAYMENT_INTERVAL.YEAR) {
      res = `${this.paymentIntervalCount}年払い`;
    } else if (this.paymentInterval === PAYMENT_INTERVAL.MONTH) {
      res = `${this.paymentIntervalCount}ヶ月払い`;
    } else if (this.paymentInterval === PAYMENT_INTERVAL.DAY) {
      res = `${this.paymentIntervalCount}日払い`;
    }
    return res;
  }

  expiresAtText(format = "YYYY/MM/DD") {
    return Subscription.getDateText(this.expiresAt, format);
  }
}
/* eslint-enable */

export {
  PAYMENT_INTERVAL,
  AUTO_RENEWAL,
  SUBSCRIPTION_STATUS,
  Subscription,
};
